import {
  AccordionDetails,
  Accordion as MuiAccordion,
  AccordionSummary as MuiAccordionSummary,
  Typography,
} from '@mui/material'
import { makeStyles, withStyles } from 'tss-react/mui'
import { ExpandMore } from '@mui/icons-material'
import {
  ElementType,
  ReactElement,
  ReactNode,
  KeyboardEventHandler,
} from 'react'
import { FormattedMessage } from 'react-intl'

// Common Accordion
// Provides default styling in alignment with current design system:
// https://www.figma.com/design/Qakbf0ck9WM6GT0rkMvihB/Design-System-%2F-Component-Library?node-id=10554-21663&node-type=frame&t=HUh8PHLJrYliAPLw-0
//
// Accepts a custom accordion summary component while still providing core accordion functionality and best practices

const useStyles = makeStyles()((theme) => {
  return {
    accordionsWrapper: {
      '& div:not(:last-child)': {
        borderBottom: 'none',
      },
    },
    accordionBorder: {
      borderBottom: `1px solid ${theme.palette.grey[400]}`,
      borderTop: `1px solid ${theme.palette.grey[400]}`,
    },
  }
})

const Accordion = withStyles(
  MuiAccordion,
  (theme, _params, classes) => ({
    root: {
      boxShadow: 'none',
      '&:before': {
        display: 'none',
      },
      [`&.${classes.expanded}`]: {
        margin: 'auto',
        paddingBottom: theme.spacing(3),
      },
    },
  }),
  { name: 'CustomCommonAccordion', uniqId: 'CstmAcrd01' }
)

const AccordionSummary = withStyles(
  MuiAccordionSummary,
  (theme, _params, classes) => ({
    root: {
      borderRadius: 0,
      minHeight: 48,
      display: 'flex',
      [`&.${classes.expanded}`]: {
        minHeight: 48,
      },
      '&.Mui-focusVisible': {
        '& h2': {
          color: theme.palette.common.white,
        },
        '& .MuiSvgIcon-root': {
          color: theme.palette.common.white,
        },
      },
    },
    content: {
      [`&.${classes.expanded}`]: {
        margin: theme.spacing(3, 0),
      },
    },
  }),
  { name: 'CustomAccordionSummary', uniqId: 'CstmAcrdSumry01' }
)

// Coordinates styling for multiple accordions
// Can be utilized for handling multiple accordion state, if/when needed
export const CommonAccordionsWrapper = ({ children }) => {
  const { classes } = useStyles()

  return <div className={classes.accordionsWrapper}>{children}</div>
}

type FormattedMessageType = ElementType<typeof FormattedMessage>

interface CommonAccordionProps {
  summary: ReactElement<FormattedMessageType> | ReactElement
  summaryId: string // required for a11y best practices
  summaryAriaControls: string // required for a11y best practices
  details: ReactNode // accordion content
  defaultExpanded?: boolean
  expandedCallback?: Function
  overrideClasses?: {
    accordion?: string
    summary?: string
    details?: string
  }
  // Optional improvement to performance by restricting accordion content mounting until expanded
  // See: https://v4.mui.com/components/accordion/#performance for more details
  unmountOnExit?: boolean
  expanded?: boolean
  onKeyDown?: KeyboardEventHandler<HTMLDivElement>
}

const CommonAccordion = ({
  summary,
  summaryId,
  summaryAriaControls,
  details,
  defaultExpanded = false,
  expandedCallback = () => ({}),
  overrideClasses = {},
  unmountOnExit = false,
  expanded,
  onKeyDown,
}: CommonAccordionProps) => {
  const { classes, cx } = useStyles()
  // If a FormattedMessage is passed in the component will default to design system styling
  // otherwise, it will render a fully custom summary component
  const shouldDisplayDefaultStyles = summary?.type === FormattedMessage

  // Allows parent components that use CommonAccordion to react to expansion state changes
  const onChangeCallbackHandler = (
    event: React.ChangeEvent<{}>,
    isExpanded: boolean
  ) => {
    expandedCallback(isExpanded)
  }

  return (
    <Accordion
      classes={{ root: overrideClasses.accordion }}
      className={cx({
        [classes.accordionBorder]: shouldDisplayDefaultStyles,
      })}
      defaultExpanded={defaultExpanded}
      square
      onChange={onChangeCallbackHandler}
      TransitionProps={{ unmountOnExit }}
      expanded={expanded}
    >
      {shouldDisplayDefaultStyles && (
        <AccordionSummary
          classes={{ root: overrideClasses.summary }}
          expandIcon={<ExpandMore color="primary" />}
          id={summaryId}
          aria-controls={summaryAriaControls}
        >
          <Typography variant="h2">{summary}</Typography>
        </AccordionSummary>
      )}
      {!shouldDisplayDefaultStyles && (
        // Does not use styled AccordionSummary to allow for custom appearance
        <MuiAccordionSummary
          classes={{ root: overrideClasses.summary }}
          id={summaryId}
          aria-controls={summaryAriaControls}
          onKeyDown={onKeyDown}
        >
          {summary}
        </MuiAccordionSummary>
      )}
      <AccordionDetails classes={{ root: overrideClasses.details }}>
        {details}
      </AccordionDetails>
    </Accordion>
  )
}

export default CommonAccordion
